import React from 'react';
import { HbrDomTable } from '@harbor/elements-react-standalone';

import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import type { EducationItem } from '../../../hooks/education-completion-hooks';
import { round2 } from '../../../support/enablementLeader';

export interface SeriesRow {
  id: string;
  category: string;
  series: string;
  modules: string;
  percent: string;
  status: string;
  assigned: string;
  due: React.ReactNode;
  certified: string;
}

/** Progress as a percent, over REQUIRED modules when the series has them. */
export function progressPercent(it: {
  total_required_modules?: number | null;
  completed_required_modules?: number | null;
  percent_completed?: number | null;
}): number {
  if (typeof it.total_required_modules === 'number' && it.total_required_modules > 0) {
    return round2(((it.completed_required_modules ?? 0) / it.total_required_modules) * 100);
  }
  return round2(Number(it.percent_completed) || 0);
}

/** `YYYY-MM-DD` as a short local date ("Nov 20, 2026"), without the day shifting across time zones. */
export function formatIsoDate(iso?: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The Due Date cell: the date, or an em dash. A date worked out from the module's rule says so on hover. */
function dueCell(it: EducationItem): React.ReactNode {
  const text = formatIsoDate(it.due_date);
  if (!text) return '—';
  return it.due_date_source === 'rule' ? (
    <span title="Calculated from the module's due-date rule">{text}</span>
  ) : (
    text
  );
}

/** One assigned series as a table row. */
export const seriesRowFor =
  (category: string) =>
  (it: EducationItem): SeriesRow => {
    const percentValue = progressPercent(it);
    return {
      id: `s_${it.series_id || it.series_name}`,
      category,
      series: it.series_name || it.series_id || 'Untitled series',
      modules: `${it.completed_modules} of ${it.total_modules}`,
      percent: `${percentValue}%`,
      status:
        it.status === 'completed' ? 'Complete' : it.status === 'in_progress' ? 'In progress' : 'Not started',
      assigned: formatIsoDate(it.assigned_on) ?? '—',
      due: dueCell(it),
      certified: it.certified ? it.certificate_name || 'Certified' : '—',
    };
  };

const column = (id: string, name: string, width: number, align = ColumnAlignment.Left): Column =>
  getColumnTypeConfig({
    id,
    name,
    width,
    columnType: ColumnType.Custom,
    sortable: false,
    align,
    dataType: DataType.String,
    resizable: false,
    reorderable: false,
  } as Column);

const COLUMNS: Column[] = [
  column('category', 'Scope', 110),
  column('series', 'Series', 300),
  column('modules', 'Modules', 110, ColumnAlignment.Right),
  column('percent', 'Progress', 90, ColumnAlignment.Right),
  column('status', 'Status', 110),
  column('assigned', 'Assigned', 110),
  column('due', 'Due Date', 110),
  column('certified', 'Certification', 160),
].map((c) => ({ ...c, resizable: false, reorderable: false }));

const RIGHT_ALIGNED = new Set(['modules', 'percent']);

/** The per-series education table shared by the individual page and the manager's per-report dropdowns. */
export default function EducationSeriesTable({ rows }: { rows: SeriesRow[] }) {
  return (
    <HbrDomTable autoLayout={true} columns={COLUMNS} data={rows}>
      {COLUMNS.map((c) => (
        <div key={`scol-${c.id}`} slot={`column-${c.id}`}>
          <div className="w-max">{c.name}</div>
        </div>
      ))}
      {rows.flatMap((row) =>
        COLUMNS.map((c) => {
          const slot = `row-${row.id}-column-${c.id}`;
          return (
            <div
              key={slot}
              slot={slot}
              className="min-h-8 flex items-center box-content"
              style={{
                justifyContent: RIGHT_ALIGNED.has(c.id) ? 'flex-end' : 'flex-start',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {(row as any)[c.id]}
            </div>
          );
        }),
      )}
    </HbrDomTable>
  );
}
